import withStyles from 'isomorphic-style-loader/withStyles';
import React, { createRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import WindowScroller from 'react-virtualized/dist/commonjs/WindowScroller';
import { VariableSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { computeContainerWidth } from '../../../utils/gridHelpers';
import LoadingWidget from '../../LoadingWidget';
import * as infiniteListStyles from './InfiniteList.scss';
const ItemRenderer = SortableElement(({ itemIndex: index, style, data }) => {
    var _a;
    const { componentType, handleSelected, isDynamicRowHeightEnabled, items, itemsViewedRef, loading, rowHeight, setState, getMaxIndex, setMaxIndex, } = data;
    const ComponentType = componentType;
    const itemData = (_a = items[index]) !== null && _a !== void 0 ? _a : { placeholder: true };
    const isInitialRender = index > getMaxIndex();
    useEffect(() => {
        if (isInitialRender)
            setMaxIndex(index);
    }, [index, isInitialRender, setMaxIndex]);
    const onMouseEnter = (event) => {
        setState({ rowHoverIndex: index });
        const { target } = event;
        if (target) {
            target.firstChild.style.height = 'auto';
            setState({ rowHoverHeight: target.firstChild.clientHeight });
        }
    };
    const onMouseLeave = () => {
        setState({ rowHoverIndex: null, rowHoverHeight: rowHeight });
    };
    return (React.createElement("div", { onMouseEnter: isDynamicRowHeightEnabled ? onMouseEnter : undefined, onMouseLeave: isDynamicRowHeightEnabled ? onMouseLeave : undefined },
        React.createElement(ComponentType, { ref: itemsViewedRef, data: itemData, handleSelected: handleSelected, style: style, index: index, isInitialRender: isInitialRender, loading: loading })));
});
class InfiniteList extends React.Component {
    constructor(props) {
        super(props);
        this.infiniteLoaderCallback = () => {
            var _a;
            if ((_a = this.props) === null || _a === void 0 ? void 0 : _a.isResizableRowList) {
                return ({ onItemsRendered, ref }) => (React.createElement("div", { className: infiniteListStyles.autoSizeContainer },
                    React.createElement(AutoSizer, null, ({ width, height }) => (React.createElement("div", { ref: ref, className: infiniteListStyles.removeOutline }, this.renderList({
                        onItemsRendered,
                        width,
                        height,
                    }))))));
            }
            return ({ onItemsRendered, ref }) => (React.createElement("div", { ref: ref, className: infiniteListStyles.removeOutline }, this.renderList({
                onItemsRendered,
                height: window.innerHeight,
            })));
        };
        this.getMaxIndex = () => this.maxIndex.current;
        this.setMaxIndex = (index) => {
            var _a;
            this.maxIndex.current = Math.max((_a = this.maxIndex.current) !== null && _a !== void 0 ? _a : -1, index);
        };
        this.setStateCallback = (obj) => this.setState(obj);
        this.renderList = (listProps) => {
            const { onItemsRendered, width, height } = listProps;
            const { windowWidth, rowHeight, data, componentType, handleSelected, isDynamicRowHeightEnabled, isResizableRowList, itemsViewedRef, loading, scrollToIndex, } = this.props;
            const { items } = data;
            const { rowHoverIndex, rowHoverHeight } = this.state;
            const containerWidth = isResizableRowList ? width : computeContainerWidth(windowWidth || 0);
            const count = items.length;
            const itemSize = (index) => isDynamicRowHeightEnabled && index === rowHoverIndex ? rowHoverHeight : rowHeight;
            const listItemKey = (index, itemData) => index + itemData.items[index].primaryText;
            const listItemData = {
                componentType,
                handleSelected,
                isDynamicRowHeightEnabled,
                items,
                itemsViewedRef,
                loading,
                rowHeight,
                setState: this.setStateCallback,
                getMaxIndex: this.getMaxIndex,
                setMaxIndex: this.setMaxIndex,
            };
            return (React.createElement(VariableSizeList, { className: !isResizableRowList ? infiniteListStyles.list : {}, ref: this.listRef, height: height, width: containerWidth, itemCount: count, itemKey: listItemKey, itemData: listItemData, estimatedItemSize: rowHeight, itemSize: itemSize, onItemsRendered: onItemsRendered, initialScrollOffset: scrollToIndex * rowHeight, overscanCount: 5 }, this.renderItem));
        };
        this.renderItem = ({ index, style, data }) => (React.createElement(ItemRenderer, { index: index, itemIndex: index, style: style, data: data, disabled: !this.props.isDragAndDropEnabled }));
        this.setState({
            rowHoverIndex: null,
            rowHoverHeight: 0,
        });
        this.listRef = createRef();
        this.maxIndex = createRef();
        this.maxIndex.current = -1;
    }
    componentDidUpdate(prevProps, prevState) {
        var _a, _b, _c;
        if (((_a = this.props) === null || _a === void 0 ? void 0 : _a.isResizableRowList) &&
            ((_b = this.props) === null || _b === void 0 ? void 0 : _b.isDynamicRowHeightEnabled) &&
            this.state.rowHoverIndex !== prevState.rowHoverIndex) {
            (_c = this.listRef.current) === null || _c === void 0 ? void 0 : _c.resetAfterIndex(0);
        }
    }
    render() {
        var _a, _b, _c;
        const { onEndOfWidget, items } = this.props.data;
        const count = items.length;
        // Functions to check when to load more items.
        const loadMore = () => this.props.handleSelected(onEndOfWidget);
        // InfiniteLoader will call loadMoreRows callback when isRowLoaded returns false.
        // We now say the last row is "not loaded" so InfiniteLoader will call loadMoreRows
        // when we get to the last row, if we know onEndOfWidget exists.
        const isItemLoaded = (index) => index < ((onEndOfWidget === null || onEndOfWidget === void 0 ? void 0 : onEndOfWidget.length) ? items.length - 1 : items.length);
        const showLoader = (onEndOfWidget === null || onEndOfWidget === void 0 ? void 0 : onEndOfWidget.length) > 0;
        const visualPlayQueueClass = ((_a = this.props) === null || _a === void 0 ? void 0 : _a.isVisualPlayQueue)
            ? infiniteListStyles.visualPlayQueue
            : '';
        const textRowClass = ((_b = this.props) === null || _b === void 0 ? void 0 : _b.isTextRow) ? infiniteListStyles.isTextRow : '';
        const resizableRowClass = ((_c = this.props) === null || _c === void 0 ? void 0 : _c.isResizableRowList)
            ? infiniteListStyles.isResizableRowList
            : '';
        const infiniteListStyle = [
            infiniteListStyles.infiniteList,
            visualPlayQueueClass,
            textRowClass,
            resizableRowClass,
        ].join(' ');
        const infiniteLoader = (React.createElement(InfiniteLoader, { isItemLoaded: isItemLoaded, itemCount: count, loadMoreItems: loadMore }, this.infiniteLoaderCallback()));
        const onScroll = ({ scrollTop }) => { var _a; return (_a = this.listRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo(scrollTop); };
        const windowScroller = React.createElement(WindowScroller, { onScroll: onScroll }, () => React.createElement("div", null));
        return (React.createElement("div", { className: infiniteListStyle },
            windowScroller,
            infiniteLoader,
            showLoader && React.createElement(LoadingWidget, null)));
    }
}
function mapStateToProps(state) {
    return {
        windowWidth: state.BrowserState.windowWidth,
    };
}
function mapDispatchToProps(dispatch) {
    return {};
}
const SortableVirtualList = withStyles(infiniteListStyles)(SortableContainer(connect(mapStateToProps, mapDispatchToProps)(InfiniteList)));
export default class SortableComponent extends React.Component {
    constructor(props) {
        super(props);
        this.registerListRef = function (sortableList, listInstance) {
            if (this) {
                this.List = listInstance;
            }
            if (sortableList) {
                // TODO refactor this
                // eslint-disable-next-line no-param-reassign
                sortableList.List = listInstance;
            }
        };
        this.isDragAndDropEnabled = () => !!(this.props.handleReorder && this.props.handleReorder.length > 0);
        this.setState({ isDragging: false });
    }
    render() {
        var _a, _b, _c, _d, _e;
        if ((_a = this.state) === null || _a === void 0 ? void 0 : _a.isDragging) {
            (_c = (_b = this.List) === null || _b === void 0 ? void 0 : _b.container) === null || _c === void 0 ? void 0 : _c.classList.add(infiniteListStyles.isDragging);
        }
        else {
            (_e = (_d = this.List) === null || _d === void 0 ? void 0 : _d.container) === null || _e === void 0 ? void 0 : _e.classList.remove(infiniteListStyles.isDragging);
        }
        return (React.createElement(SortableVirtualList, Object.assign({ ref: this.registerListRef.bind(null, this) }, this.props, { transitionDuration: 100, onSortStart: this.dragStart.bind(this), onSortEnd: this.dragEnd.bind(this), helperClass: infiniteListStyles.dragHelper, distance: 15, 
            // @ts-ignore
            isDragAndDropEnabled: this.isDragAndDropEnabled(), useWindowAsScrollContainer: true, hideSortableGhost: true, lockAxis: "y" })));
    }
    dragStart() {
        this.setState({ isDragging: true });
    }
    dragEnd({ oldIndex, newIndex }) {
        var _a, _b, _c;
        this.setState({ isDragging: false });
        if (newIndex === oldIndex) {
            return;
        }
        (_b = (_a = this.props).handleReorder) === null || _b === void 0 ? void 0 : _b.call(_a, newIndex, oldIndex);
        (_c = document.activeElement) === null || _c === void 0 ? void 0 : _c.blur();
    }
}
